<template>
  <div
      class="form-optiongroup-radio"
      role="radiogroup"
      :aria-labelledby="props.ariaLabelledBy"
      :key="props.unique"
  >
    <div
        tabindex="0"
        v-for="option in props.options"
        :key="option.value"
        class="form-option-radio"
        :class="{ 'is-selected': props.modelValue === option.value }"
        role="radio"
        :aria-label="option.name"
        :aria-checked="props.modelValue === option.value"
        @click="() => selectOption(option.value)"
        @keyup.enter="() => selectOption(option.value)"
        @keyup.space="() => selectOption(option.value)"
    >
      <slot name="option" :option="option" :selected="props.modelValue === option.value">
        <!-- Default fallback -->
        <img v-if="option.image" :src="option.image" alt="" aria-hidden="true" />
        <p>{{ option.name }}</p>
      </slot>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits } from 'vue';

const props = defineProps({
  options: { type: Array, required: true },
  modelValue: { required: true },
  ariaLabelledBy: { type: String, default: null },
  unique : {type: String, default: "question"}
});

const emit = defineEmits(['update:modelValue', 'optionSelected']);

const selectOption = (value) => {
  emit('update:modelValue', value);
  emit('optionSelected', value);
};
</script>

<style scoped>

  /* --- Allgemeine Radiobox-Stile --- */
   .form-optiongroup-radio {
    display: grid;
    gap: 8px;
    padding-bottom: 20px;
    grid-template-columns: repeat(1, 1fr);

    /* Responsive Grid-Logik */
    @media (min-width: 1200px) {
      grid-template-columns: repeat(auto-fit, minmax(min(18%, 600px), 1fr));
    }

    @media (max-width: 1199px) and (min-width: 992px) {
      grid-template-columns: repeat(auto-fit, minmax(min(30%, 600px), 1fr));
    }

    @media (max-width: 991px) and (min-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 767px) {
      grid-template-columns: 1fr;
    }
  }

  .form-option-radio {
    background-color: var(--form-layoutcard-bg-default);
    position: relative;
    outline: none;
    overflow: hidden;
    border: var(--form-layoutcard-border-default);
    border-radius: var(--form-layoutcard-radius-default);
    transition: var(--form-layoutcard-hover-transition);

    /* Zustände: Hover & Selektiert */
    /* 1. NUR wenn die Option wirklich ausgewählt ist (gilt für alle Geräte) */
    &.is-selected {
      border: 1px solid var(--form-layoutcard-color-selected);
      background-color: var(--form-layoutcard-bg-default);
      box-shadow: inset 0 0 0 1px var(--form-layoutcard-color-selected);
      transition: var(--form-layoutcard-hover-transition);
    }

    /* 2. NUR wenn der User eine echte Maus benutzt (verhindert den iOS-Geister-Rahmen) */
    @media (hover: hover) {
      &:hover {
        border: 1px solid var(--form-layoutcard-color-hover);
        background-color: var(--form-layoutcard-bg-default);
        box-shadow: inset 0 0 0 1px var(--form-layoutcard-color-hover);
        transition: var(--form-layoutcard-hover-transition);
      }
      &.is-selected:hover {
        border: 1px solid var(--form-layoutcard-color-selected);
        box-shadow: inset 0 0 0 1px var(--form-layoutcard-color-selected);
      }
    }

    &:active,
    &.is-selected:active {
      background-color: var(--form-layoutcard-bg-press);
      box-shadow: inset 0 0 0 1px var(--form-layoutcard-color-press);
      transition: var(--form-layoutcard-active-transition);
    }

    /* Fokus-Management */
    &:focus-visible {
      /*box-shadow: none;*/
      outline: var(--form-layoutcard-border-focus);
      outline-offset: var(--form-layoutcard-focus-offset);
      border-radius: var(--form-layoutcard-radius-focus);
    }

    &:focus:not(:focus-visible) {
      outline: none;
    }

    @supports not selector(:focus-visible) {
      &:focus {
        box-shadow: none;
        outline: var(--form-layoutcard-border-focus);
        outline-offset: var(--form-layoutcard-focus-offset);
        border-radius: var(--form-layoutcard-radius-focus);
      }
    }
    }

    /* Label-Struktur */
  .form-option-radio :slotted(label) {
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    cursor: pointer;
    box-sizing: content-box !important;
    height: 175px;
    padding: 20px 44px;
  }

  .form-option-radio :slotted(label img) {
        height: 100px;
        width: 100px;
        margin-bottom: 10px;
        margin-top: 10px;
        object-fit: contain;
        flex-shrink: 0;
      }



      /* Mobile Anpassungen innerhalb des Labels */
      @media (max-width: 767px) {
        .form-option-radio :slotted(label) {
          padding: 0.6rem 1.2rem;
          flex-direction: row;
          align-items: center;
          justify-content: flex-start;
          height: auto;
          text-align: left;
        }

        .form-option-radio :slotted(label img) {
          height: 40px;
          width: 40px;
          margin-right: 1rem;
          margin-bottom: 0;
          margin-top: 0;
        }

        .form-option-radio :slotted(label p) {
          font-size: 1em;
          line-height: 1.3;
        }
      }



</style>

